/**
 * Hộp "Tải ảnh gốc" góc dưới phải — gắn ở vỏ app (`routes/_app.tsx`) nên đi
 * trang nào cũng còn trong lúc mẻ nạp ảnh của đơn đang chạy
 * (xem `photo-upload-job.ts`). Nhiều đơn tải song song thì mỗi đơn một thẻ,
 * xếp chồng theo thứ tự bắt đầu.
 */

import { Link } from "@tanstack/react-router";
import { UploadCloud } from "lucide-react";
import { formatEta, formatSpeed } from "@/lib/upload-meter";
import { useOrderPhotoUpload, type OrderPhotoUploadJob } from "../photo-upload-job";
import { useWarnBeforeLeave } from "@/hooks/useWarnBeforeLeave";

export function OrderPhotoUploadPopup() {
  const jobs = Object.values(useOrderPhotoUpload((s) => s.jobs));
  useWarnBeforeLeave(jobs.length > 0);
  if (jobs.length === 0) return null;
  return (
    // Nhiều thẻ trên điện thoại thì cuộn trong khung, không phủ kín màn.
    <div
      data-testid="photo-upload-stack"
      className="fixed bottom-4 right-4 z-50 flex max-h-[60vh] w-[min(360px,calc(100vw-2rem))] flex-col gap-2 overflow-y-auto"
    >
      {jobs.map((job) => (
        <UploadCard key={job.orderId} job={job} />
      ))}
    </div>
  );
}

function UploadCard({ job }: { job: OrderPhotoUploadJob }) {
  const pct = job.total > 0 ? Math.round((job.done / job.total) * 100) : 0;
  const verb =
    job.phase === "compress" ? "Đang nén" : job.phase === "retry" ? "Mạng chập chờn — chờ gửi lại" : "Đang tải lên";
  return (
    <div
      data-testid="photo-upload-progress"
      role="status"
      aria-live="polite"
      className="shrink-0 rounded-md border border-border bg-surface p-3 text-sm shadow-lg"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <UploadCloud className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="font-medium text-foreground">Tải ảnh gốc</p>
            <p className="text-xs tabular-nums text-muted-foreground">
              {job.done}/{job.total}
            </p>
          </div>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {verb}: {job.currentName}
          </p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-muted-foreground">
            <p>
              Đã nạp {job.uploaded} ảnh
              {job.failed > 0 ? ` · lỗi ${job.failed}` : ""}
            </p>
            <p data-testid="photo-upload-eta" className="shrink-0 tabular-nums">
              {job.speed === null || job.eta === null
                ? "Đang ước tính thời gian…"
                : `${formatSpeed(job.speed)} · ${formatEta(job.eta)}`}
            </p>
          </div>
          {job.orderCode ? (
            <Link
              to="/orders/$orderId"
              params={{ orderId: job.orderId }}
              search={{ tab: "photo" }}
              className="mt-1 inline-flex items-center text-[11px] text-primary hover:underline"
            >
              Đơn {job.orderCode}
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
